import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "@/lib/i18n";
import { buildPreviewUrl, buyerOrigin } from "@/modules/manage/lib/previewUrl";

export type PreviewDevice = "desktop" | "phone";

/** The two widths the six skins are actually drawn for. */
const WIDTH: Record<PreviewDevice, number> = { desktop: 1180, phone: 390 };

/**
 * The editor's canvas: the seller's own storefront, rendering their DRAFT.
 *
 * A real preview of the real page, not a mock of it. Everything a restaurant
 * theme does — the phone ordering flow, the category rail, the dotted leaders,
 * the desktop poster — lives in the buyer app, and any reimplementation here
 * would be a second thing to keep in step and the one that goes wrong.
 *
 * ## Scaled, never squeezed
 *
 * The frame renders at its TRUE width and is transform-scaled to fit. Sizing the
 * iframe to the pane instead would put the storefront at whatever width the pane
 * happens to be, so a seller checking their desktop page would be shown the
 * tablet breakpoint and believe it.
 *
 * ## Double-buffered, so a reload is never visible
 *
 * Copy and structure cannot be patched into a live restaurant skin — only the
 * palette can — so those changes need the page back from the server. Swapping
 * the iframe's `key` did that by destroying the document and building a new one,
 * which meant a white flash on every edit: typing a headline made the whole
 * storefront blink once per pause.
 *
 * So a reload mounts a SECOND frame underneath, hidden, and the two swap only
 * once the new one has said `preview:ready`. The seller keeps looking at a fully
 * painted page the entire time; the reload happens behind it. If the incoming
 * frame never reports (a slow page, a dropped request), a timeout promotes it
 * anyway — a stale preview is worse than one late flash.
 */
export function StorefrontPreview({
  storeSlug,
  locale,
  token,
  version,
  device,
  selectedSectionId = null,
  onSelect,
  onFields,
  onUnpatched,
  livePatch = null,
}: {
  storeSlug: string;
  locale: string;
  /** `null` while the token is being minted, or if minting failed. */
  token: string | null;
  version: number;
  device: PreviewDevice;
  /** Highlighted in the canvas, and re-focused after a reload. */
  selectedSectionId?: string | null;
  /** Fired when the seller clicks a section IN the page. */
  onSelect?: (sectionId: string, sectionType: string | null) => void;
  /**
   * What the frame reports about its tagged fields: `fields` are the
   * `sectionId:fieldId` pairs currently on screen at this width, `known` is
   * every pair the page tagged at all. The editor filters only within `known`,
   * so a field no skin has marked up yet keeps its control.
   */
  onFields?: (report: { fields: string[]; known: string[] }) => void;
  /**
   * Fields the live patch declined to write, because the skin prints them as
   * markup rather than as one string (STACK's hero splits the headline a word
   * to a line and colours one of them). Overwriting those would flatten the
   * design for a second; the editor reloads for them instead, behind the
   * double buffer, so nothing flickers.
   */
  onUnpatched?: (fields: string[]) => void;
  /**
   * A draft to push into the live frame without reloading it. The bridge applies
   * the colour tokens to the document immediately, so a palette change repaints
   * rather than round-trips. It cannot rebuild restaurant MARKUP, so copy and
   * structure still bump `version`.
   */
  livePatch?: { draft: unknown; at: number } | null;
}) {
  const t = useTranslate();
  const paneRef = useRef<HTMLDivElement | null>(null);
  const origin = useMemo(() => buyerOrigin({ storeSlug }), [storeSlug]);
  const [pane, setPane] = useState({ width: 0, height: 0 });

  /*
   * The frame the seller is LOOKING at, which lags the requested one while a
   * reload paints. `shown` only ever moves forward once a newer frame is ready.
   */
  const requested = `${device}-${version}`;
  const [shown, setShown] = useState(requested);
  const frames = useRef(new Map<string, HTMLIFrameElement | null>());

  useEffect(() => {
    const node = paneRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.contentRect;
      if (box) setPane({ width: box.width, height: box.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /*
   * The bridge speaks first. `preview:ready` is the only reliable moment to
   * configure it: posting into the frame before its listener exists is silently
   * dropped, and `onLoad` fires before the bridge's effects run.
   *
   * The message is matched to a frame by its `source` window, because two frames
   * are alive during a swap and the message carries no id of its own.
   */
  useEffect(() => {
    if (!origin) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      const data = event.data as {
        type?: string;
        sectionId?: string;
        sectionType?: string | null;
        fields?: unknown;
        known?: unknown;
      };
      if (data?.type === "preview:ready") {
        for (const [key, frame] of frames.current) {
          if (frame && frame.contentWindow === event.source) {
            setShown(key);
            return;
          }
        }
        return;
      }
      /* A click in the OLD frame while a new one is painting still means what
         it says, so selection is not filtered by which frame sent it. */
      if (data?.type === "preview:select" && data.sectionId) {
        onSelect?.(data.sectionId, data.sectionType ?? null);
      }
      if (data?.type === "preview:unpatched" && Array.isArray(data.fields)) {
        onUnpatched?.(data.fields as string[]);
        return;
      }
      if (data?.type === "preview:fields" && Array.isArray(data.fields)) {
        onFields?.({
          fields: data.fields as string[],
          known: Array.isArray(data.known) ? (data.known as string[]) : [],
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origin, onSelect, onFields, onUnpatched]);

  /*
   * The promotion of last resort. A frame that never reports ready — a slow
   * render, a dropped request, a bridge that failed to mount — would otherwise
   * leave the seller looking at a preview that quietly stopped updating, which
   * is a worse failure than the flash this component exists to avoid.
   */
  useEffect(() => {
    if (shown === requested) return;
    const timer = setTimeout(() => setShown(requested), 4000);
    return () => clearTimeout(timer);
  }, [shown, requested]);

  const post = useCallback(
    (message: unknown) => {
      if (!origin) return;
      frames.current.get(shown)?.contentWindow?.postMessage(message, origin);
    },
    [origin, shown],
  );

  useEffect(() => {
    /* The simple-editor behaviour: no floating toolbar over the page — the panel
       beside the canvas is the toolbar. Re-asserted on every swap, which is also
       how the seller's place is kept: `editor:selection` asks the bridge to
       scroll that section back into view. */
    post({ type: "editor:chrome", hideToolbar: true });
    post({ type: "editor:selection", sectionId: selectedSectionId ?? null });
    /* Asked on every swap, because the answer is per-DOCUMENT and per-width:
       a device change mounts a different frame and the visible set changes
       with it. */
    post({ type: "editor:fields" });
  }, [shown, selectedSectionId, post]);

  useEffect(() => {
    if (!livePatch) return;
    post({ type: "draft:update", draft: livePatch.draft, locale });
  }, [livePatch, locale, post]);

  const frameWidth = WIDTH[device];
  // Never scale UP: a phone frame blown to 3× on a wide monitor is a caricature
  // of the page, not a preview of it.
  const scale = pane.width > 0 ? Math.min(1, pane.width / frameWidth) : 0;
  // The frame is as tall as the pane allows, so the seller scrolls the STORE
  // rather than the editor.
  const frameHeight = scale > 0 ? Math.max(320, pane.height / scale) : 0;

  /* Both frames stay mounted through a swap; the incoming one is invisible and
     untouchable until it is the one being shown. */
  const keys = shown === requested ? [shown] : [shown, requested];

  if (!token || scale === 0) {
    return (
      <div
        ref={paneRef}
        className="relative h-full w-full overflow-hidden"
        style={{ background: "var(--muted)" }}
      >
        <p className="flex h-full items-center justify-center p-6 text-center text-sm text-[color:var(--muted-foreground)]">
          {token === null
            ? t((m) => m.manage.storefrontPreviewUnavailable)
            : t((m) => m.manage.storefrontPreviewLoading)}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={paneRef}
      className="relative h-full w-full overflow-hidden"
      style={{ background: "var(--muted)" }}
    >
      {/* Centred by FLEX, scaled about its own top-centre. Combining a
          `translateX(-50%)` with the scale in one transform does not centre it:
          the translate applies in the element's unscaled coordinate space and
          overshoots by half the difference. */}
      <div className="absolute inset-0 flex justify-center">
        <div
          style={{
            width: frameWidth,
            height: frameHeight,
            transform: `scale(${scale})`,
            transformOrigin: "top center",
            flex: "0 0 auto",
            position: "relative",
          }}
        >
          {keys.map((key) => {
            const visible = key === shown;
            const [keyDevice, keyVersion] = key.split("-");
            return (
              <iframe
                key={key}
                ref={(node) => {
                  if (node) frames.current.set(key, node);
                  else frames.current.delete(key);
                }}
                src={buildPreviewUrl({
                  storeSlug,
                  locale,
                  token,
                  version: Number(keyVersion),
                })}
                title={t((m) => m.manage.storefrontPreviewTitle)}
                // The incoming frame must not steal clicks or the tab order
                // while it is invisible.
                tabIndex={visible ? undefined : -1}
                aria-hidden={visible ? undefined : true}
                data-device={keyDevice}
                className="absolute inset-0 block h-full w-full border-0"
                style={{
                  background: "#fff",
                  opacity: visible ? 1 : 0,
                  pointerEvents: visible ? "auto" : "none",
                  zIndex: visible ? 1 : 0,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
